// Jobs Hub Landing Page — job board focused with hero card
'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { trackCampaignView, trackBrowseJobsClick, trackSignupStart } from '@/lib/tracking';
import { initializeTracking } from '@/lib/tracking';
import { Sparkles, Zap, Briefcase, MapPin, Users, MessageCircle, Shield, Camera } from 'lucide-react';

export default function JobBoardLandingPage() {
  useEffect(() => {
    initializeTracking();
    trackCampaignView('Job Board');
  }, []);

  return (
    <div className="min-h-screen bg-[#080412]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[20%] -left-[15%] w-[500px] h-[500px] rounded-full blur-[140px] bg-[#924890] opacity-20 animate-[hero-drift_14s_ease-in-out_infinite]" />
          <div className="absolute -bottom-[20%] -right-[10%] w-[450px] h-[450px] rounded-full blur-[130px] bg-[#E11A50] opacity-15 animate-[hero-drift-r_18s_ease-in-out_infinite]" />
          <div className="absolute top-[40%] right-[30%] w-[250px] h-[250px] rounded-full blur-[100px] bg-[#ECBA6A] opacity-10" />
        </div>
        {/* Noise */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[.04] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
          }}
        />

        <div className="relative z-10 max-w-lg mx-auto px-4 py-16 md:py-24">
          {/* Card */}
          <div className="relative">
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden opacity-50">
              <div className="absolute inset-0 w-[150%] h-[150%] -top-[25%] -left-[25%] animate-[hero-border-rotate_6s_linear_infinite]"
                style={{ background: 'conic-gradient(from 0deg, #ECBA6A, #E11A50, #924890, #3D1552, #924890, #E11A50, #ECBA6A)' }}
              />
            </div>
            <div className="relative rounded-2xl bg-[#0e0820]/80 backdrop-blur-xl border border-white/[.06] shadow-[0_24px_80px_-12px_rgba(146,72,144,.25)]">
              <div className="px-8 py-10 sm:px-10 sm:py-12 flex flex-col items-center text-center gap-6">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.2em] text-[#ECBA6A]/70">
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>Job Board</span>
                  <Briefcase className="h-3.5 w-3.5" />
                </div>

                <h1 className="font-js text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight">
                  <span className="js-text-fill">Industry</span>
                  <br />
                  <span className="text-white">Job Board</span>
                </h1>

                <p className="max-w-sm text-[15px] leading-relaxed text-white/50">
                  Where artists, talent, and producers connect on paid work, scene collaborations, and production services.
                </p>

                <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#ECBA6A]/40 to-transparent" />

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <Link
                    href="/posts"
                    onClick={() => trackBrowseJobsClick()}
                    className="group relative inline-flex items-center justify-center gap-2 rounded-xl w-full sm:w-auto
                               px-7 py-3.5 font-bold text-[15px] text-white overflow-hidden
                               bg-gradient-to-r from-[#E11A50] via-[#EF5D7B] to-[#924890]
                               shadow-[0_8px_32px_-8px_rgba(225,26,80,.5)]
                               hover:shadow-[0_12px_40px_-8px_rgba(225,26,80,.6)]
                               hover:scale-[1.02] active:scale-[.98]
                               transition-all duration-300"
                  >
                    <Zap className="h-4 w-4" />
                    <span>Browse Jobs</span>
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  </Link>
                  <Link
                    href="/register"
                    onClick={() => trackSignupStart('Job Board', 'job-board-signup')}
                    className="inline-flex items-center justify-center rounded-xl w-full sm:w-auto
                               px-7 py-3.5 font-semibold text-[15px]
                               text-white/70 border border-white/10 bg-white/[.04]
                               hover:bg-white/[.08] hover:border-white/20 hover:text-white
                               transition-all duration-300"
                  >
                    Post a Listing
                  </Link>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 pt-1">
                  {[
                    { icon: MapPin, label: 'By City' },
                    { icon: Shield, label: 'Verified' },
                    { icon: Users, label: 'Free' },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-white/25">
                      <Icon className="h-3 w-3" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 buckets — primary entry into the board */}
      <section className="py-16 px-4 border-t border-white/[.06]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-3 text-white/90">Four Ways to Use the Board</h2>
          <p className="text-center text-white/50 mb-10 max-w-xl mx-auto text-sm">
            Whether you're hiring, available for work, looking for scene partners, or selling production services — pick a lane.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: Briefcase,
                title: 'Hire',
                desc: 'Producers, studios, and creators bringing talent on for paid productions.',
                href: '/posts?type=hiring',
                cta: 'Browse who’s hiring',
              },
              {
                icon: Camera,
                title: 'Hire me',
                desc: 'Performers, crew, and editors openly available for paid bookings.',
                href: '/posts?type=looking',
                cta: 'Browse available talent',
              },
              {
                icon: Users,
                title: 'Collab',
                desc: 'Scene partners and creative collaborators teaming up on shared projects.',
                href: '/posts?type=connect',
                cta: 'Browse collabs',
              },
              {
                icon: Sparkles,
                title: 'Services',
                desc: 'Industry pros offering production services by the project — editing, studios, customs, photography, coaching, and more.',
                href: '/posts?type=services',
                cta: 'Browse services',
              },
            ].map(({ icon: Icon, title, desc, href, cta }) => (
              <Link
                key={title}
                href={href}
                onClick={() => trackBrowseJobsClick()}
                className="group rounded-xl bg-white/[.03] border border-white/[.06] p-6 hover:bg-white/[.06] hover:border-white/[.15] hover:shadow-[0_12px_40px_-12px_rgba(225,26,80,.25)] transition-all duration-300 block"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#E11A50]/20 to-[#924890]/10 text-[#ECBA6A]">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-bold text-lg text-white/95">{title}</h3>
                </div>
                <p className="text-white/50 text-sm leading-relaxed mb-4">{desc}</p>
                <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#ECBA6A]/80 group-hover:text-[#ECBA6A] transition-colors">
                  {cta}
                  <span className="transition-transform duration-300 group-hover:translate-x-0.5">&#8594;</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* For Talent / For Producers */}
      <section className="py-16 px-4 border-t border-white/[.06]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl bg-white/[.03] border border-white/[.06] p-8">
            <h2 className="text-xl font-bold mb-4 text-white/90">For Talent</h2>
            <ul className="space-y-3 text-white/50 text-sm mb-6">
              {[
                'Create a profile showcasing your portfolio',
                'Post your availability and preferred rates',
                'Browse casting calls in your area',
                'Get messaged by producers directly',
              ].map((text) => (
                <li key={text} className="flex items-start gap-2">
                  <span className="text-[#ECBA6A] mt-0.5 shrink-0">&#10003;</span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <Link
              href="/register"
              onClick={() => trackSignupStart('Job Board', 'talent-signup')}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-sm
                         bg-gradient-to-r from-[#E11A50] to-[#924890] text-white
                         hover:shadow-[0_8px_24px_-8px_rgba(225,26,80,.4)]
                         transition-all duration-300"
            >
              <Zap className="h-3.5 w-3.5" />
              Create Your Profile
            </Link>
          </div>

          <div className="rounded-xl bg-white/[.03] border border-white/[.06] p-8">
            <h2 className="text-xl font-bold mb-4 text-white/90">For Producers</h2>
            <ul className="space-y-3 text-white/50 text-sm mb-6">
              {[
                'Post casting calls and job listings',
                'Search talent by location, look, and experience',
                'View portfolios with photos and videos',
                'Contact talent directly through the platform',
              ].map((text) => (
                <li key={text} className="flex items-start gap-2">
                  <span className="text-[#ECBA6A] mt-0.5 shrink-0">&#10003;</span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
            <Link
              href="/posts"
              onClick={() => trackBrowseJobsClick()}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold text-sm
                         text-white/80 border border-white/10 bg-white/[.04]
                         hover:bg-white/[.08] hover:text-white
                         transition-all duration-300"
            >
              Browse Talent
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-4 border-t border-white/[.06]">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-white/90">Ready to Get Started?</h2>
          <p className="text-white/40 mb-8">Free to join. Browse listings or create your own.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/posts"
              className="group relative inline-flex items-center gap-2 rounded-xl
                         px-8 py-3.5 font-bold text-[15px] text-white overflow-hidden
                         bg-gradient-to-r from-[#E11A50] via-[#EF5D7B] to-[#924890]
                         shadow-[0_8px_32px_-8px_rgba(225,26,80,.5)]
                         hover:scale-[1.02] active:scale-[.98]
                         transition-all duration-300"
            >
              Browse Jobs
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            </Link>
            <Link
              href="/register"
              onClick={() => trackSignupStart('Job Board', 'bottom-signup')}
              className="inline-flex items-center rounded-xl px-8 py-3.5 font-semibold text-[15px]
                         text-white/70 border border-white/10 bg-white/[.04]
                         hover:bg-white/[.08] hover:text-white
                         transition-all duration-300"
            >
              Sign Up Free
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
